import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import { Upload, LogOut, Calendar, MapPin, ImageIcon, Trash2, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function OrganizerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const load = () => api.get("/organizer/photos").then((res) => setData(res.data));

  useEffect(() => {
    load().catch(() => toast.error("Failed to load"));
  }, []);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    setProgress(0);
    setErrors([]);
    try {
      const res = await api.post(`/organizer/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        timeout: 600000,
      });
      toast.success(`Uploaded ${res.data.count} photos. OCR running.`);
      if (res.data.errors?.length) setErrors(res.data.errors);
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("drag-active");
    handleFiles(e.dataTransfer.files);
  };

  const handleLogout = () => { logout(); navigate("/"); };

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><p className="font-mono text-sm text-[#475569]">LOADING…</p></div>;
  }

  const ev = data.event;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="organizer-dashboard">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline kicker">ORGANIZER · {user?.name?.toUpperCase()}</span>
            <button onClick={handleLogout} className="text-xs font-semibold inline-flex items-center gap-1 bg-[#0F172A] hover:bg-black text-white px-3 py-2" data-testid="org-logout-btn">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {ev ? (
          <>
            <p className="kicker mb-2">YOUR ASSIGNED EVENT</p>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tighter" data-testid="org-event-name">{ev.name}</h1>
            <div className="mt-3 flex items-center flex-wrap gap-4 text-xs text-[#475569]">
              <span className="inline-flex items-center gap-1.5"><Calendar size={12} />{ev.event_date}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin size={12} />{ev.city}, {ev.country}</span>
              <span className="inline-flex items-center gap-1.5"><ImageIcon size={12} />{data.photos.length} photos uploaded</span>
              {ev.status === "published" && (
                <a href={`/events/${ev.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#5B4CFF] hover:underline">
                  <ExternalLink size={12} /> Public page
                </a>
              )}
            </div>

            <div
              ref={dropRef}
              onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("drag-active"); }}
              onDragLeave={() => dropRef.current?.classList.remove("drag-active")}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className="dropzone cursor-pointer p-10 md:p-14 text-center bg-white mt-8 mb-6"
              data-testid="org-dropzone"
            >
              <Upload size={28} className="mx-auto text-[#5B4CFF] mb-4" />
              <p className="font-display text-lg font-semibold text-[#0F172A]">
                {uploading ? `Uploading… ${progress}%` : "Drop race photos here or click to upload"}
              </p>
              <p className="mt-1 text-sm text-[#475569]">JPG, PNG, WEBP · Bib numbers detected automatically</p>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="org-file-input"
              />
              {uploading && (
                <div className="mt-4 max-w-md mx-auto bg-[#F8FAFC] border border-[#E2E8F0] h-2 overflow-hidden">
                  <div className="h-full bg-[#5B4CFF]" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>

            {errors.length > 0 && (
              <div className="mb-6 border border-[#EF4444]/40 bg-[#fef2f2] p-4 text-xs text-[#0F172A]">
                <p className="font-semibold mb-2 inline-flex items-center gap-2"><AlertCircle size={14} className="text-[#EF4444]" /> Some files failed:</p>
                <ul className="list-disc pl-5 space-y-1">
                  {errors.map((e, i) => <li key={i}>{e.filename}: {e.error}</li>)}
                </ul>
              </div>
            )}

            {data.photos.length === 0 ? (
              <div className="border border-dashed border-[#E2E8F0] p-12 text-center bg-white">
                <p className="text-sm text-[#475569]">No photos uploaded yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="org-photo-grid">
                {data.photos.map((p) => (
                  <div key={p.id} className="group relative aspect-square border border-[#E2E8F0] bg-[#F8FAFC] overflow-hidden">
                    <img src={fileUrl(p.id)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 flex items-end p-2">
                      <div className="flex flex-wrap gap-1">
                        {p.bib_numbers?.length ? p.bib_numbers.slice(0, 3).map((b) => (
                          <span key={b} className="bg-[#5B4CFF] text-white text-[10px] font-mono px-1.5 py-0.5">#{b}</span>
                        )) : (
                          <span className="bg-white/80 text-[#475569] text-[10px] font-mono px-1.5 py-0.5">no bib</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="border border-dashed border-[#E2E8F0] p-12 text-center bg-white">
            <p className="font-display text-xl text-[#0F172A]">No event assigned</p>
            <p className="mt-2 text-sm text-[#475569]">Contact your race admin.</p>
          </div>
        )}
      </main>
    </div>
  );
}
